import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

// A desktop-width miniature of a real page.
//
// The city page is a wide layout, and the editor gives its preview half a
// screen. Rendering it into that column would show the phone layout and tell an
// organizer nothing about what they are actually making, so the page is laid
// out at full width and scaled down instead.
//
// Media queries still resolve against the viewport rather than this box, which
// is what we want: on the desktop the editor is used from, the preview shows
// the desktop layout.
export default function PagePreviewFrame({
  width = 1280,
  children,
}: {
  width?: number;
  children: ReactNode;
}) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const box = outer.current;
    const content = inner.current;
    if (!box || !content) return;

    // A transform doesn't change layout, so the frame has to be told how tall
    // the scaled content is or it leaves a screen of empty space below it.
    const measure = () => {
      setScale(Math.min(1, box.clientWidth / width));
      setHeight(content.scrollHeight);
    };

    measure();
    // ResizeObserver reports the layout box, which the scale doesn't touch -
    // so this settles rather than feeding itself.
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(content);
    return () => observer.disconnect();
  }, [width, children]);

  return (
    <div
      ref={outer}
      // shrink-0 because the frame sits in the editor's scrolling flex column:
      // without it the frame is squashed to the column's height and the bottom
      // of the page is clipped away with nothing to scroll to.
      className="border-line shadow-card shrink-0 overflow-hidden border-2 bg-white"
    >
      <div style={{ height: height * scale }}>
        <div
          ref={inner}
          style={{
            width,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
